import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import ScrollToTop from './components/ScrollToTop';
import PullToRefresh from './components/PullToRefresh';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { APP_REFRESH_EVENT } from './hooks/useAppRefresh';
import { App as CapApp } from '@capacitor/app';
import { checkForUpdate } from './services/liveUpdate';
import { AuthProvider, useAuth } from './context/AuthContext';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useTimesheetReminder } from './hooks/useTimesheetReminder';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LicenseGate from './components/auth/LicenseGate';
import WebOnlyRoute from './components/auth/WebOnlyRoute';
import MovedRoute from './components/auth/MovedRoute';
import AccountsOrNurseryGate from './components/accounts/AccountsOrNurseryGate';
import { retailAlso } from './hooks/useStockSeat';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import Landing from './pages/Landing';
import FeaturePage from './pages/public/FeaturePage';
import Pricing from './pages/public/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import PlatformTermsGate from './components/legal/PlatformTermsGate';
import OrgChangedNotice from './components/ui/OrgChangedNotice';
import UpdateAvailableBanner from './components/ui/UpdateAvailableBanner';
import ModuleTermsGate from './components/legal/ModuleTermsGate';
import Dashboard from './pages/Dashboard';
import Timesheet from './pages/Timesheet';
import TimesheetHistoryPage from './pages/TimesheetHistory';
import MyTimesheet from './pages/MyTimesheet';
import ResumeWork from './pages/ResumeWork';
import AdminManagement from './pages/AdminManagement';
import JoinOrganisation from './pages/JoinOrganisation';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import HRManagement from './pages/HRManagement';
import StaffProfile from './pages/StaffProfile';
import AwardReviews from './pages/hr/AwardReviews';
import HRLiveDashboard from './pages/HRLiveDashboard';
import PayCycleDashboard from './pages/PayCycleDashboard';
import CompanyPolicies from './pages/CompanyPolicies';
import WhsDashboard from './pages/hr/whs/WhsDashboard';
import WhsReport from './pages/hr/whs/WhsReport';
import WhsTrackReport from './pages/hr/whs/WhsTrackReport';
import WhsReports from './pages/hr/whs/WhsReports';
import WhsReportDetail from './pages/hr/whs/WhsReportDetail';
import WhsRiskAssessments from './pages/hr/whs/WhsRiskAssessments';
import WhsRiskAssessmentDetail from './pages/hr/whs/WhsRiskAssessmentDetail';
import WhsActions from './pages/hr/whs/WhsActions';
import WhsInspections from './pages/hr/whs/WhsInspections';
import WhsInspectionDetail from './pages/hr/whs/WhsInspectionDetail';
import WhsAudits from './pages/hr/whs/WhsAudits';
import WhsAuditDetail from './pages/hr/whs/WhsAuditDetail';
import WhsDocuments from './pages/hr/whs/WhsDocuments';
import WhsTraining from './pages/hr/whs/WhsTraining';
import WhsEmergencyPlans from './pages/hr/whs/WhsEmergencyPlans';
import WhsObjectives from './pages/hr/whs/WhsObjectives';
import WhsManagementReviews from './pages/hr/whs/WhsManagementReviews';
import WhsSettings from './pages/hr/whs/WhsSettings';
import MyCertifications from './pages/certifications/MyCertifications';
import CertificationsAdmin from './pages/certifications/CertificationsAdmin';
import InductionGate from './components/induction/InductionGate';
import LanguageSync from './i18n/LanguageSync';
import InductionsHub from './pages/inductions/InductionsHub';
import Compliance from './pages/compliance/Compliance';
import EngagementDetail from './pages/compliance/EngagementDetail';
import AuditorPortal from './pages/audit/AuditorPortal';
import InductionDetail from './pages/inductions/InductionDetail';
import InductionPerson from './pages/inductions/InductionPerson';
import MyInductions from './pages/inductions/MyInductions';
import ReadPolicy from './pages/inductions/ReadPolicy';
import ReadEquipmentInduction from './pages/inductions/ReadEquipmentInduction';
import EmailVerification from './pages/EmailVerification';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import ResendVerification from './pages/ResendVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MfaChallenge from './pages/MfaChallenge';
import MfaSettings from './pages/settings/MfaSettings';
import OrgSecuritySettings from './pages/admin/OrgSecuritySettings';
import CropTypesAdmin from './pages/admin/CropTypesAdmin';
import CultivarAdmin from './pages/cultivar/CultivarAdmin';
import ProgramsAdmin from './pages/cultivar/ProgramsAdmin';
import ProgramPriceVersions from './pages/cultivar/ProgramPriceVersions';
import ProgramContractTemplates from './pages/cultivar/ProgramContractTemplates';
import OrderList from './pages/cultivar/OrderList';
import OrderCreate from './pages/cultivar/OrderCreate';
import OrderDetail from './pages/cultivar/OrderDetail';
import ProgramApprovers from './pages/cultivar/ProgramApprovers';
import ApprovalQueue from './pages/cultivar/ApprovalQueue';
import SupplyClaimQueue from './pages/cultivar/SupplyClaimQueue';
import NurseryOffers from './pages/cultivar/NurseryOffers';
import SublicenceGroups from './pages/cultivar/SublicenceGroups';
import OrderApiKeys from './pages/cultivar/OrderApiKeys';
import AttributeDefinitionsManager from './pages/cultivar/AttributeDefinitionsManager';
import GrowingRegionsManager from './pages/cultivar/GrowingRegionsManager';
import MaturityChart from './pages/cultivar/MaturityChart';
import Approvals from './pages/Approvals';
import PayrollManager from './pages/PayrollManager';
import WorkScheduleSettings from './pages/WorkScheduleSettings';
import PayrollExportMappings from './pages/PayrollExportMappings';
import MyobSettings from './pages/MyobSettings';
import PublicHolidaySettings from './pages/PublicHolidaySettings';
import SoilSensorDashboard from './pages/soilSensorDashboard';
import IrrigationOverview from './pages/irrigation/IrrigationOverview';
import EspNowDashboard from './pages/irrigation/EspNowDashboard';
import FarmIrrigationDetail from './pages/irrigation/FarmIrrigationDetail';
import EspNowControllerDetail from './pages/irrigation/EspNowControllerDetail';
import EspNowAdminSettings from './pages/irrigation/EspNowAdminSettings';
import NetworkMap from './pages/irrigation/NetworkMap';
import SensorDeviceDetail from './pages/irrigation/SensorDeviceDetail';

// Orchard Management Pages
import OrchardDashboard from './pages/orchard/OrchardDashboard';
import FarmCreate from './pages/orchard/FarmCreate';
import FarmDetail from './pages/orchard/FarmDetail';
import FarmEdit from './pages/orchard/FarmEdit';
import BlockCreate from './pages/orchard/BlockCreate';
import BlockDetail from './pages/orchard/BlockDetail';
import BlockEdit from './pages/orchard/BlockEdit';
import CultivarAreaCreate from './pages/orchard/CultivarAreaCreate';
import CultivarAreaDetail from './pages/orchard/CultivarAreaDetail';
import CropLoadCount from './pages/orchard/CropLoadCount';
import CultivarAreaEdit from './pages/orchard/CultivarAreaEdit';
import SoilAnalysis from './pages/orchard/SoilAnalysis';
import SoilSampleForm from './pages/orchard/SoilSampleForm';
import SoilSampleDetail from './pages/orchard/SoilSampleDetail';
import NutrientRemoval from './pages/orchard/NutrientRemoval';
import Fertiliser from './pages/orchard/Fertiliser';
import FertiliserPlanPage from './pages/orchard/FertiliserPlanPage';

// Subscription Pages
import SubscriptionManagement from './pages/subscriptions/SubscriptionManagement';
import SubscriptionLicenseManagement from './pages/subscriptions/LicenseManagement';
import BillingConsole from './pages/billing/BillingConsole';
import BillingOnboard from './pages/billing/BillingOnboard';
import BillingOrganisation from './pages/billing/BillingOrganisation';
import PurchaseFlow from './pages/subscriptions/PurchaseFlow';
import InvoiceHistory from './pages/subscriptions/InvoiceHistory';
import XeroAdmin from './pages/subscriptions/XeroAdmin';

// Equipment Pages
import EquipmentList          from './pages/equipment/EquipmentList';
import EquipmentDetail        from './pages/equipment/EquipmentDetail';
import EquipmentForm          from './pages/equipment/EquipmentForm';
import PreStartFlow           from './pages/equipment/PreStartFlow';
import TrackingWatcher        from './components/TrackingWatcher';
import PresenceWatcher        from './components/PresenceWatcher';
import LocationDisclosureHost from './components/LocationDisclosureHost';
import MaintenanceManagement  from './pages/equipment/MaintenanceManagement';
import AttachImplement        from './pages/equipment/AttachImplement';
import SessionHistory         from './pages/equipment/SessionHistory';
import ServiceRecords         from './pages/equipment/ServiceRecords';
import EquipmentAdminSettings from './pages/equipment/EquipmentAdminSettings';
import PartsCatalog           from './pages/equipment/PartsCatalog';
import InsurancePolicies      from './pages/equipment/InsurancePolicies';
import InsurancePolicyDetail  from './pages/equipment/InsurancePolicyDetail';
import InsurancePolicyForm    from './pages/equipment/InsurancePolicyForm';

// Spray Management Pages
import SprayDashboard      from './pages/spray/SprayDashboard';
import SprayCreate         from './pages/spray/SprayCreate';
import SprayDetail         from './pages/spray/SprayDetail';import SprayEdit                from './pages/spray/SprayEdit';
import ChemicalRegistry         from './pages/spray/ChemicalRegistry';
import ChemicalDetail           from './pages/spray/ChemicalDetail';
import InventoryDashboard       from './pages/spray/InventoryDashboard';
import SprayAdminSettings       from './pages/spray/SprayAdminSettings';
import PestDiseaseAdmin         from './pages/spray/PestDiseaseAdmin';
import PestDiseaseDetail        from './pages/spray/PestDiseaseDetail';
import PurchaseOrderList         from './pages/purchaseOrders/PurchaseOrderList';
import PurchaseOrderCreate       from './pages/purchaseOrders/PurchaseOrderCreate';
import PurchaseOrderDetail       from './pages/purchaseOrders/PurchaseOrderDetail';
import PurchaseOrdersGate        from './components/purchaseOrders/PurchaseOrdersGate';
import SprayMyCredentials        from './pages/spray/SprayMyCredentials';
import SprayCalibration          from './pages/spray/SprayCalibration';
import SprayTncGate               from './components/spray/SprayTncGate';

// Weather Stations Pages
import WeatherPage from './pages/weather/WeatherPage';
import WeatherStationSetup from './pages/weather/WeatherStationSetup';
import ChillUnitsPage from './pages/weather/ChillUnitsPage';
import PestRiskDashboard from './pages/weather/PestRiskDashboard';

// Farm Jobs Pages
import JobsDashboard    from './pages/jobs/JobsDashboard';
import JobCreate        from './pages/jobs/JobCreate';
import JobDetail        from './pages/jobs/JobDetail';
import JobEdit          from './pages/jobs/JobEdit';
import JobOperatorView  from './pages/jobs/JobOperatorView';
import Workforce        from './pages/jobs/Workforce';
import Labour           from './pages/jobs/Labour';
import Insights         from './pages/jobs/Insights';
import CrewMode         from './pages/jobs/CrewMode';
import PickBins         from './pages/harvest/PickBins';
import Shed             from './pages/harvest/Shed';

// Breeder Pages
import BreederDashboard from './pages/breeder/BreederDashboard';
import BreederPlantingMap from './pages/breeder/BreederPlantingMap';
import BreederLicensors from './pages/breeder/BreederLicensors';

// Nursery Pages
import NurseryDashboard from './pages/nursery/NurseryDashboard';
import NurseryLocations from './pages/nursery/NurseryLocations';
import TreeTypeAdmin from './pages/nursery/TreeTypeAdmin';
import PropagationSetup from './pages/nursery/PropagationSetup';
import QuarantineProtocols from './pages/nursery/QuarantineProtocols';
import MaterialList from './pages/nursery/MaterialList';
import MaterialForm from './pages/nursery/MaterialForm';
import MaterialDetail from './pages/nursery/MaterialDetail';
import MotherPlants from './pages/nursery/MotherPlants';
import ProductionBoard from './pages/nursery/ProductionBoard';
import LotList from './pages/nursery/LotList';
import LotCreate from './pages/nursery/LotCreate';
import LotDetail from './pages/nursery/LotDetail';
import StockItemList from './pages/nursery/StockItemList';
import StockItemForm from './pages/nursery/StockItemForm';
import NurseryProductList from './pages/nursery/ProductList';
import NurseryProductForm from './pages/nursery/ProductForm';
import NurseryOrderList from './pages/nursery/OrderList';
import NurseryOrderCreate from './pages/nursery/OrderCreate';
import NurseryOrderDetail from './pages/nursery/OrderDetail';
import NurseryPoImport from './pages/nursery/PoImport';
import NurseryCrossDockSchedules from './pages/nursery/CrossDockSchedules';
import NurseryCrossDockScheduleDetail from './pages/nursery/CrossDockScheduleDetail';
import NurseryRetailerRange from './pages/nursery/RetailerRange';
import NurseryRetailerHub from './pages/nursery/RetailerHub';
import NurseryStockHub from './pages/nursery/StockHub';
import NurseryLogisticsHub from './pages/nursery/LogisticsHub';
import NurserySalesHub from './pages/nursery/SalesHub';
import NurseryBackorders from './pages/nursery/Backorders';
import NurseryWorkSummary from './pages/nursery/WorkSummary';
import NurseryInventoryDashboard from './pages/nursery/InventoryDashboard';
import CustomerOrdersDashboard from './pages/nursery/CustomerOrdersDashboard';
import AccountsDashboard from './pages/accounts/AccountsDashboard';
import RetailDashboard from './pages/retail/RetailDashboard';
import NurseryCustomerList from './pages/accounts/CustomerList';
import NurseryCustomerForm from './pages/accounts/CustomerForm';
import NurseryCustomerDetail from './pages/accounts/CustomerDetail';
import NurseryInvoiceList from './pages/accounts/InvoiceList';
import NurseryInvoiceDetail from './pages/accounts/InvoiceDetail';
import NurseryReceivables from './pages/accounts/Receivables';
import NurseryRebates from './pages/accounts/Rebates';
import Remittances from './pages/accounts/Remittances';
import XeroSettings from './pages/accounts/XeroSettings';
import NurseryPriceListList from './pages/accounts/PriceListList';
import NurseryPriceListForm from './pages/accounts/PriceListForm';
import NurseryWebsiteSignupsList from './pages/nursery/WebsiteSignupsList';
// TEMPORARY: delete after 2026-10-28 with its route below.
import LegacyCustomerImport from './pages/nursery/LegacyCustomerImport';
import LegacyCultivarImport from './pages/cultivar/LegacyCultivarImport';
import NurseryTradeAccountRequestsList from './pages/nursery/TradeAccountRequestsList';
import NurseryDeliveryZonesList from './pages/nursery/DeliveryZonesList';
import NurseryPickers from './pages/nursery/NurseryPickers';
import NurseryDespatchPlanner from './pages/nursery/DespatchPlanner';
import NurseryLoadList from './pages/nursery/LoadList';
import NurseryLoadDetail from './pages/nursery/LoadDetail';
import NurseryPickingLoads from './pages/nursery/PickingLoads';
import NurseryPickList from './pages/nursery/PickListPage';
import NurseryTransferList from './pages/nursery/TransferList';
import NurseryTransferDetail from './pages/nursery/TransferDetail';
import NurseryDespatchPlaces from './pages/nursery/DespatchPlaces';
import NurseryTills from './pages/nursery/Tills';
import NurseryTillDetail from './pages/nursery/TillDetail';
import CardPayments from './pages/accounts/CardPayments';
import TradingTerms from './pages/accounts/TradingTerms';
import AccountRules from './pages/accounts/AccountRules';
import Deposits from './pages/accounts/Deposits';
import Suppliers from './pages/accounts/Suppliers';
import PurchaseOrderPeople from './pages/accounts/PurchaseOrderPeople';
import CreditNoteRequests from './pages/accounts/CreditNoteRequests';
import CounterPaid from './pages/CounterPaid';
import NurseryStocktakes from './pages/nursery/Stocktakes';
import NurserySprays from './pages/nursery/NurserySprays';
import NurseryStocktakeCount from './pages/nursery/StocktakeCount';
import NurseryForecast from './pages/nursery/NurseryForecast';
import NurseryColourCodes from './pages/nursery/ColourCodes';
import NurseryPainting from './pages/nursery/Painting';

// Cultivar Registry Pages
import CultivarRegistry from './pages/cultivar/CultivarRegistry';
import CultivarCreate from './pages/cultivar/CultivarCreate';
import PbrDashboard from './pages/cultivar/pbr/PbrDashboard';
import PbrCandidates from './pages/cultivar/pbr/PbrCandidates';
import PbrApplications from './pages/cultivar/pbr/PbrApplications';
import PbrApplicationCreate from './pages/cultivar/pbr/PbrApplicationCreate';
import PbrApplicationDetail from './pages/cultivar/pbr/PbrApplicationDetail';
import QualifiedPersonsRegister from './pages/cultivar/pbr/QualifiedPersonsRegister';
import CultivarEdit from './pages/cultivar/CultivarEdit';
import CultivarDetail from './pages/cultivar/CultivarDetail';
import LicenseManagement from './pages/cultivar/LicenseManagement';
import DeclarationManagement from './pages/cultivar/DeclarationManagement';
import CertificateManagement from './pages/cultivar/CertificateManagement';
import ComplianceDashboard from './pages/cultivar/ComplianceDashboard';
import VerificationPage from './pages/cultivar/VerificationPage';
import ContractsList from './pages/cultivar/ContractsList';
import ContractDetail from './pages/cultivar/ContractDetail';
import ContractsHistory from './pages/cultivar/ContractsHistory';
import LicenseVerify from './pages/cultivar/LicenseVerify';
import LicenseAgreementView from './pages/cultivar/LicenseAgreementView';
import LicensorDashboard from './pages/cultivar/LicensorDashboard';
import IssueLicense from './pages/cultivar/IssueLicense';


function DeepLinkHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const listener = CapApp.addListener('appUrlOpen', (event: { url: string }) => {
      const url = new URL(event.url);
      if (url.pathname) {
        navigate(url.pathname);
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, [navigate]);

  // Live (over-the-air) web-bundle updates. Checked on launch and whenever the
  // app returns to the foreground; a new bundle is staged silently and applies
  // on the next cold start, never mid-session. Rate-limited inside the service.
  useEffect(() => {
    checkForUpdate();
    const sub = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkForUpdate();
    });
    return () => { sub.then(l => l.remove()); };
  }, []);

  usePushNotifications();
  useTimesheetReminder();
  return null;
}

/**
 * Forces a full unmount+remount of any component whenever the active org changes.
 * This guarantees every piece of local state is cleared — more reliable than
 * dep-array tracking alone, which can silently fail if context updates batch
 * or if the component was already showing data for the same orgId value.
 */
function useActiveOrgId() {
  const { user } = useAuth();
  return user?.orgs?.find(o => o.is_active)?.id ?? user?.organisation_id ?? 'no-org';
}

function OrgScopedDashboard() {
  const activeOrgId = useActiveOrgId();
  return <Dashboard key={activeOrgId} />;
}

function OrgScopedTimesheet() {
  const activeOrgId = useActiveOrgId();
  return <Timesheet key={activeOrgId} />;
}

function OrgScopedTimesheetHistory() {
  const activeOrgId = useActiveOrgId();
  return <TimesheetHistoryPage key={activeOrgId} />;
}

function OrgScopedMyTimesheet() {
  const activeOrgId = useActiveOrgId();
  return <MyTimesheet key={activeOrgId} />;
}


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Invalidate any active React Query data when a pull-to-refresh fires.
  // Pages using useEffect-based fetching should subscribe via useAppRefresh().
  useEffect(() => {
    const handler = () => {
      queryClient.refetchQueries({ type: 'active' });
    };
    window.addEventListener(APP_REFRESH_EVENT, handler);
    return () => window.removeEventListener(APP_REFRESH_EVENT, handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageSync />
        <Router>
          <ScrollToTop />
          <PullToRefresh />
          <DeepLinkHandler />
          <TrackingWatcher />
          <PresenceWatcher />
          <LocationDisclosureHost />
          <OrgChangedNotice />
          <UpdateAvailableBanner />
          <PlatformTermsGate />
          <InductionGate />
          <ModuleTermsGate />
          <div className="App">
            <Routes>
              {/* Public routes — redirect to login on native mobile apps */}
              <Route
                path="/"
                element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <Landing />}
              />
              <Route
                path="/features/:slug"
                element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <FeaturePage />}
              />
              <Route path="/features" element={<Navigate to="/#features" replace />} />
              <Route
                path="/pricing"
                element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <Pricing />}
              />
              
              {/* Auth routes - redirect to dashboard if already authenticated */}
              <Route 
                path="/login" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <LoginForm />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/register" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <RegisterForm />
                  </ProtectedRoute>
                } 
              />
              
              {/* Email verification and password reset routes */}
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* MFA challenge — unauthenticated; gated by mfaTicket in route state */}
              <Route path="/mfa-challenge" element={<MfaChallenge />} />

              {/* MFA self-service settings */}
              <Route
                path="/settings/security"
                element={
                  <ProtectedRoute>
                    <MfaSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/security"
                element={
                  <ProtectedRoute>
                    <OrgSecuritySettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin"
                element={
                  <ProtectedRoute>
                    <CultivarAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/crop-types"
                element={
                  <ProtectedRoute>
                    <CropTypesAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/programs"
                element={
                  <ProtectedRoute>
                    <ProgramsAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/programs/:id/price-versions"
                element={
                  <ProtectedRoute>
                    <ProgramPriceVersions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/programs/:id/contract-templates"
                element={
                  <ProtectedRoute>
                    <ProgramContractTemplates />
                  </ProtectedRoute>
                }
              />
              <Route path="/cultivar/orders"     element={<ProtectedRoute><OrderList /></ProtectedRoute>} />
              <Route path="/cultivar/orders/new" element={<ProtectedRoute><OrderCreate /></ProtectedRoute>} />
              <Route path="/cultivar/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
              <Route path="/cultivar/admin/programs/:id/approvers" element={<ProtectedRoute><ProgramApprovers /></ProtectedRoute>} />
              <Route path="/cultivar/approvals" element={<ProtectedRoute><ApprovalQueue /></ProtectedRoute>} />
              <Route path="/cultivar/supply-claims" element={<ProtectedRoute><SupplyClaimQueue /></ProtectedRoute>} />
              <Route path="/cultivar/nursery-offers" element={<ProtectedRoute><NurseryOffers /></ProtectedRoute>} />
              <Route path="/cultivar/sublicence-groups" element={<ProtectedRoute><SublicenceGroups /></ProtectedRoute>} />
              <Route path="/cultivar/maturity-chart" element={<ProtectedRoute><MaturityChart /></ProtectedRoute>} />
              <Route path="/cultivar/orders/api-keys" element={<ProtectedRoute><OrderApiKeys /></ProtectedRoute>} />
              <Route
                path="/cultivar/admin/growing-regions"
                element={
                  <ProtectedRoute>
                    <GrowingRegionsManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/admin/attributes"
                element={
                  <ProtectedRoute>
                    <AttributeDefinitionsManager />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <OrgScopedDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/timesheet" 
                element={
                  <ProtectedRoute>
                    <OrgScopedTimesheet />
                  </ProtectedRoute>
                } 
              />
              
              <Route
                path="/timesheet/history"
                element={
                  <ProtectedRoute>
                    <OrgScopedTimesheetHistory />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/timesheet/my-timesheet"
                element={
                  <ProtectedRoute>
                    <OrgScopedMyTimesheet />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/work/resume"
                element={
                  <ProtectedRoute>
                    <ResumeWork />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/approvals"
                element={
                  <ProtectedRoute>
                    <Approvals />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payroll"
                element={
                  <ProtectedRoute>
                    <PayrollManager />
                  </ProtectedRoute>
                }
              />

              {/* An invitation sent to an address that already has an account */}
              <Route
                path="/join-organisation/:token"
                element={
                  <ProtectedRoute>
                    <JoinOrganisation />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/super-admin"
                element={
                  <ProtectedRoute>
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/work-schedule"
                element={
                  <ProtectedRoute>
                    <WorkScheduleSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/public-holidays"
                element={
                  <ProtectedRoute>
                    <PublicHolidaySettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr"
                element={
                  <ProtectedRoute>
                    <HRManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/live-dashboard"
                element={
                  <ProtectedRoute>
                    <HRLiveDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/pay-cycle-dashboard"
                element={
                  <ProtectedRoute>
                    <PayCycleDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/company-policies"
                element={
                  <ProtectedRoute>
                    <CompanyPolicies />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/staff"
                element={
                  <ProtectedRoute>
                    <StaffProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr/award-reviews"
                element={
                  <ProtectedRoute>
                    <AwardReviews />
                  </ProtectedRoute>
                }
              />

              {/* Leave entitlements moved onto the employee profile's Leave tab. */}
              <Route path="/hr/leave-entitlements" element={<Navigate to="/hr/staff?tab=leave" replace />} />

              {/* Workplace Health & Safety — worker reporting (any authenticated
                  user) + the HR-gated management console. */}
              <Route path="/whs/report" element={<ProtectedRoute><WhsReport /></ProtectedRoute>} />
              <Route path="/whs/track" element={<ProtectedRoute><WhsTrackReport /></ProtectedRoute>} />
              <Route path="/hr/whs" element={<ProtectedRoute><WhsDashboard /></ProtectedRoute>} />
              <Route path="/hr/whs/reports" element={<ProtectedRoute><WhsReports /></ProtectedRoute>} />
              <Route path="/hr/whs/reports/:id" element={<ProtectedRoute><WhsReportDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/risk-assessments" element={<ProtectedRoute><WhsRiskAssessments /></ProtectedRoute>} />
              <Route path="/hr/whs/risk-assessments/:id" element={<ProtectedRoute><WhsRiskAssessmentDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/actions" element={<ProtectedRoute><WhsActions /></ProtectedRoute>} />
              <Route path="/hr/whs/inspections" element={<ProtectedRoute><WhsInspections /></ProtectedRoute>} />
              <Route path="/hr/whs/inspections/:id" element={<ProtectedRoute><WhsInspectionDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/audits" element={<ProtectedRoute><WhsAudits /></ProtectedRoute>} />
              <Route path="/hr/whs/audits/:id" element={<ProtectedRoute><WhsAuditDetail /></ProtectedRoute>} />
              <Route path="/hr/whs/documents" element={<ProtectedRoute><WhsDocuments /></ProtectedRoute>} />
              <Route path="/hr/whs/training" element={<ProtectedRoute><WhsTraining /></ProtectedRoute>} />
              <Route path="/hr/whs/emergency-plans" element={<ProtectedRoute><WhsEmergencyPlans /></ProtectedRoute>} />
              <Route path="/hr/whs/objectives" element={<ProtectedRoute><WhsObjectives /></ProtectedRoute>} />
              <Route path="/hr/whs/management-reviews" element={<ProtectedRoute><WhsManagementReviews /></ProtectedRoute>} />
              <Route path="/hr/whs/settings" element={<ProtectedRoute><WhsSettings /></ProtectedRoute>} />

              {/* Staff Certifications & Licenses register */}
              <Route
                path="/certifications"
                element={
                  <ProtectedRoute>
                    <MyCertifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/certifications/admin"
                element={
                  <ProtectedRoute>
                    <CertificationsAdmin />
                  </ProtectedRoute>
                }
              />

              {/* Inductions. The register pages gate themselves on any of the
                  seats that sign people off (farm_jobs supervisor/admin,
                  vehicles creator/admin) or the HR seat; "My inductions" is
                  every member's own record and needs no seat. */}
              <Route path="/inductions" element={<ProtectedRoute><InductionsHub /></ProtectedRoute>} />
              {/* Compliance & audit — gated in-page on the seats that manage each section. */}
              <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
              <Route path="/compliance/audits/:id" element={<ProtectedRoute><EngagementDetail /></ProtectedRoute>} />
              <Route path="/inductions/equipment/:id" element={<ProtectedRoute><InductionDetail /></ProtectedRoute>} />
              <Route path="/inductions/people/:ref" element={<ProtectedRoute><InductionPerson /></ProtectedRoute>} />
              <Route path="/my-inductions" element={<ProtectedRoute><MyInductions /></ProtectedRoute>} />
              <Route path="/my-inductions/policies/:id" element={<ProtectedRoute><ReadPolicy /></ProtectedRoute>} />
              <Route path="/my-inductions/equipment/:id" element={<ProtectedRoute><ReadEquipmentInduction /></ProtectedRoute>} />

              {/* Subscription Management */}
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <SubscriptionManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/:subscriptionId/licenses"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <SubscriptionLicenseManagement />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/purchase"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <PurchaseFlow />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/invoices"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <InvoiceHistory />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              {/* Platform billing console (billing_manager / super_admin). The
                  old super-admin subscriptions page lived at /subscriptions/admin. */}
              <Route path="/subscriptions/admin" element={<Navigate to="/billing" replace />} />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <BillingConsole />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/onboard"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <BillingOnboard />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/organisations/:orgId"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <BillingOrganisation />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/xero"
                element={
                  <ProtectedRoute>
                    <WebOnlyRoute>
                      <XeroAdmin />
                    </WebOnlyRoute>
                  </ProtectedRoute>
                }
              />
  
              <Route
                path="/soil-sensors"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensors & Irrigation">
                      <SoilSensorDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/irrigation"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensors & Irrigation">
                      <IrrigationOverview />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* One farm's live irrigation — what a grower opens from the
                  overview. Devices sit behind it, not in front of it. */}
              <Route
                path="/irrigation/farms/:farmId"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensors & Irrigation">
                      <FarmIrrigationDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/irrigation/network"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation Network">
                      <NetworkMap />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* ESP-NOW Irrigation Routes */}
              <Route
                path="/irrigation/espnow/admin"
                element={
                  <ProtectedRoute>
                    {/* Device management needs the irrigation *admin* seat — the
                        same gate the backend registry routes enforce. Without the
                        licenseType a plain irrigation user reaches the page and
                        only finds out on the first 403. */}
                    <LicenseGate
                      moduleKey="irrigation"
                      moduleName="Irrigation Device Management"
                      licenseType="admin"
                    >
                      <EspNowAdminSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/irrigation/espnow/:controllerId"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation Management">
                      <EspNowControllerDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/irrigation/espnow"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Irrigation Management">
                      <EspNowDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Sensor Devices (tank level + generic sensors).
                  Device management was consolidated into /irrigation/espnow/admin. */}
              <Route
                path="/irrigation/sensors/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="irrigation" moduleName="Sensor Devices">
                      <SensorDeviceDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* /irrigation/sensors was merged into /irrigation/espnow — redirect. */}
              <Route
                path="/irrigation/sensors"
                element={<Navigate to="/irrigation/espnow" replace />}
              />

              {/* Farms (spatial setup) — free for every authenticated user.
                  No subscription or license required. Paid gating lives on
                  Spray and Farm Jobs for what you do with the farms. */}
              <Route path="/orchard" element={<ProtectedRoute><OrchardDashboard /></ProtectedRoute>} />
              <Route path="/orchard/fertiliser" element={<ProtectedRoute><Fertiliser /></ProtectedRoute>} />
              <Route path="/orchard/fertiliser/plans/:id" element={<ProtectedRoute><FertiliserPlanPage /></ProtectedRoute>} />
              <Route path="/orchard/soil" element={<ProtectedRoute><SoilAnalysis /></ProtectedRoute>} />
              <Route path="/orchard/soil/new" element={<ProtectedRoute><SoilSampleForm /></ProtectedRoute>} />
              <Route path="/orchard/soil/removal" element={<ProtectedRoute><NutrientRemoval /></ProtectedRoute>} />
              <Route path="/orchard/soil/:id" element={<ProtectedRoute><SoilSampleDetail /></ProtectedRoute>} />
              <Route path="/orchard/soil/:id/edit" element={<ProtectedRoute><SoilSampleForm /></ProtectedRoute>} />
              <Route path="/orchard/farms/new" element={<ProtectedRoute><FarmCreate /></ProtectedRoute>} />
              <Route path="/orchard/farms/:id" element={<ProtectedRoute><FarmDetail /></ProtectedRoute>} />
              <Route path="/orchard/farms/:id/edit" element={<ProtectedRoute><FarmEdit /></ProtectedRoute>} />
              <Route path="/orchard/blocks/new" element={<ProtectedRoute><BlockCreate /></ProtectedRoute>} />
              <Route path="/orchard/blocks/:id" element={<ProtectedRoute><BlockDetail /></ProtectedRoute>} />
              <Route path="/orchard/blocks/:id/edit" element={<ProtectedRoute><BlockEdit /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/new" element={<ProtectedRoute><CultivarAreaCreate /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/:id" element={<ProtectedRoute><CultivarAreaDetail /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/:id/edit" element={<ProtectedRoute><CultivarAreaEdit /></ProtectedRoute>} />
              <Route path="/orchard/cultivar-areas/:id/crop-load/:countId" element={<ProtectedRoute><CropLoadCount /></ProtectedRoute>} />

              {/* Cultivar Registry Routes */}
              <Route
                path="/cultivar"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CultivarRegistry />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/create"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CultivarCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CultivarDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CultivarEdit />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* License Management */}
              <Route
                path="/cultivar/licenses"
                element={
                  <ProtectedRoute>
                    <LicenseManagement />
                  </ProtectedRoute>
                }
              />

              {/* Declaration Management */}
              <Route
                path="/cultivar/declarations"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <DeclarationManagement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Certificate Management */}
              <Route
                path="/cultivar/certificates"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <CertificateManagement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Compliance Dashboard */}
              <Route
                path="/cultivar/compliance"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <ComplianceDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* PBR Workflow — place BEFORE /cultivar/:id so 'pbr' doesn't match :id */}
              <Route
                path="/cultivar/pbr"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/candidates"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrCandidates />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/applications"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrApplications />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/applications/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrApplicationCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/applications/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <PbrApplicationDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cultivar/pbr/qualified-persons"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                      <QualifiedPersonsRegister />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* PUBLIC Certificate Verification (no auth required) */}
              <Route path="/verify/:token" element={<VerificationPage />} />

              {/* PUBLIC Privacy Policy — reachable by app stores and unauthenticated users */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              {/* PUBLIC: where a customer's phone lands after paying at the counter by QR code */}
              <Route path="/paid" element={<CounterPaid />} />
              {/* PUBLIC external auditor portal: link + emailed code, no account */}
              <Route path="/audit/:token" element={<AuditorPortal />} />

              {/* Licensing workflow */}
              {/* Contracts replaced licence templates; old links land on the list. */}
              <Route path="/cultivar/licence-templates" element={<Navigate to="/cultivar/contracts" replace />} />
              {/* TEMPORARY: the legacy cultivar import, switched off 2026-10-28.
                  It checks its own Cultivar Admin seat. */}
              <Route path="/cultivar/legacy-import" element={
                <ProtectedRoute>
                  <LegacyCultivarImport />
                </ProtectedRoute>
              } />
              <Route path="/cultivar/contracts" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <ContractsList />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/contracts/history" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <ContractsHistory />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/contracts/:id" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <ContractDetail />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              {/* PUBLIC: the QR code on every licence PDF points here. */}
              <Route path="/cultivar/verify-license/:token" element={<LicenseVerify />} />
              <Route path="/cultivar/licences/:id/agreement" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <LicenseAgreementView />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/licensor" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <LicensorDashboard />
                  </LicenseGate>
                </ProtectedRoute>
              } />
              <Route path="/cultivar/:id/licences/new" element={
                <ProtectedRoute>
                  <LicenseGate moduleKey="cultivar" moduleName="Cultivar Registry">
                    <IssueLicense />
                  </LicenseGate>
                </ProtectedRoute>
              } />

              {/* Spray Management Routes. SprayTncGate overlays the blocking T&C
                  acceptance modal on any spray page until the user accepts the
                  current active version. Backend write endpoints also enforce
                  this via the requireSprayTncAccepted middleware. */}
              <Route
                path="/spray"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayDashboard /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayCreate /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/chemicals"
                element={
                  <ProtectedRoute>
                    <ChemicalRegistry />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/chemicals/:id"
                element={
                  <ProtectedRoute>
                    <ChemicalDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/inventory"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><InventoryDashboard /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/admin"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayAdminSettings /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/credentials/me"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayMyCredentials /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Purchase orders are their own section, off the Dashboard. A spray
                  Operator or Creator raises, sends and receives; an Accounts
                  seat reads. They used to be a tab of Spray. */}
              <Route
                path="/purchase-orders"
                element={
                  <ProtectedRoute>
                    <PurchaseOrdersGate><PurchaseOrderList /></PurchaseOrdersGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-orders/new"
                element={
                  <ProtectedRoute>
                    <PurchaseOrdersGate raising><PurchaseOrderCreate /></PurchaseOrdersGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-orders/:id"
                element={
                  <ProtectedRoute>
                    <PurchaseOrdersGate><PurchaseOrderDetail /></PurchaseOrdersGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/spray/purchase-orders" element={<MovedRoute to="/purchase-orders" />} />
              <Route path="/spray/purchase-orders/new" element={<MovedRoute to="/purchase-orders/new" />} />
              <Route path="/spray/purchase-orders/:id" element={<MovedRoute to="/purchase-orders/:id" />} />
              <Route
                path="/spray/pests"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestDiseaseAdmin /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/pests/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestDiseaseDetail /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/pests/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><PestDiseaseDetail /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Calibrating a machine on its own, with no spray attached. The
                  T&C gate is needed as much as anywhere else: recording a
                  calibration is a write, and the backend enforces the same
                  requireSprayTncAccepted on it. */}
              <Route
                path="/spray/calibration"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayCalibration /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayDetail /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spray/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <SprayTncGate><SprayEdit /></SprayTncGate>
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Weather Routes — forecast is free, station data needs the subscription */}
              <Route
                path="/weather"
                element={
                  <ProtectedRoute>
                    <WeatherPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather/:stationId"
                element={
                  <ProtectedRoute>
                    <WeatherPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather/chill"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="weather_stations" moduleName="Weather Stations">
                      <ChillUnitsPage />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather/pests"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="spray" moduleName="Spray Management">
                      <PestRiskDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weather-stations/setup"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="weather_stations" moduleName="Weather Stations">
                      <WeatherStationSetup />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Farm Jobs Routes */}
              <Route
                path="/jobs"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobsDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/workforce"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Workforce />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Pickers have no seat: the API lets anyone on the job start bins. */}
              <Route
                path="/harvest/pick"
                element={
                  <ProtectedRoute>
                    <PickBins />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/harvest/shed"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Shed />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/insights"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Insights />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/labour"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <Labour />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id/crew"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs" licenseType={['supervisor', 'creator', 'admin']}>
                      <CrewMode />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="farm_jobs" moduleName="Farm Jobs">
                      <JobEdit />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs/:id/operate"
                element={
                  // No seat: anyone may run a job they are on (a pre-start's
                  // quick job above all). The server refuses anyone else's.
                  <ProtectedRoute>
                    <JobOperatorView />
                  </ProtectedRoute>
                }
              />

              {/* Breeder / Variety Owner */}
              <Route
                path="/breeder"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="breeder" moduleName="Breeder / Variety Owner">
                      <BreederDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/breeder/licensors"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="breeder" moduleName="Breeder / Variety Owner">
                      <BreederLicensors />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/breeder/cultivars/:id/map"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="breeder" moduleName="Breeder / Variety Owner">
                      <BreederPlantingMap />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Nursery Routes */}
              <Route
                path="/nursery"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <NurseryDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/production"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <ProductionBoard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/lots"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <LotList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/lots/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <LotCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/spray"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurserySprays />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/lots/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <LotDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/locations"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <NurseryLocations />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/tree-types"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <TreeTypeAdmin />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/quarantine"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <QuarantineProtocols />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/propagation/setup"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <PropagationSetup />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/material"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MaterialList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/material/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MaterialForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/material/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MaterialDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/mothers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <MotherPlants />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-items"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('operate')}>
                      <StockItemList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* TEMPORARY: the legacy customer import, switched off 2026-10-28. It
                  checks its own seats: a Nursery admin or an Accounts seat. */}
              <Route
                path="/nursery/legacy-import"
                element={
                  <ProtectedRoute>
                    <LegacyCustomerImport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/website-signups"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryWebsiteSignupsList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/trade-account-requests"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryTradeAccountRequestsList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/delivery-zones"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDeliveryZonesList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/pickers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPickers />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/despatch"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDespatchPlanner />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/loads"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryLoadList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/loads/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryLoadDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/picking"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPickingLoads />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/picking/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPickList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stocktake"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('count')}>
                      <NurseryStocktakes />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/colour-codes"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryColourCodes />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/painting"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPainting />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/forecast"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryForecast />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stocktake/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('count')}>
                      <NurseryStocktakeCount />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/transfers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryTransferList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/transfers/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryTransferDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/despatchable"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryDespatchPlaces />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/nursery/refills" element={<MovedRoute to="/nursery/despatchable" />} />
              <Route
                path="/nursery/tills"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['manager', 'admin']} retail={['staff', 'manager']} what="The tills">
                      <NurseryTills />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/tills/:id"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['manager', 'admin']} retail={['staff', 'manager']} what="The tills">
                      <NurseryTillDetail />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-items/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <StockItemForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock-items/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <StockItemForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/products"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryProductList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/products/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryProductForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/products/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryProductForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('manage')}>
                      <NurseryOrderList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('manage')}>
                      <NurseryOrderCreate />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/import"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryPoImport />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/cross-dock"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryCrossDockSchedules />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/cross-dock/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryCrossDockScheduleDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/logistics"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryLogisticsHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/work"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('admin')}>
                      <NurseryWorkSummary />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/backorders"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryBackorders />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/sales"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurserySalesHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/stock"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryStockHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/retailers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryRetailerHub />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/retailer-range"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryRetailerRange />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/orders/:id"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management" alsoModules={retailAlso('manage')}>
                      <NurseryOrderDetail />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nursery/inventory"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="nursery" moduleName="Nursery Management">
                      <NurseryInventoryDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Accounts: the money side. Customers and their terms, price lists,
                  invoices, receivables, rebates, and the Xero / MYOB connections
                  for invoices and payroll. Gated on the accounts seats; the
                  nursery reads customers and price lists, since an order is
                  taken for a customer at a price. */}
              <Route
                path="/accounts"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <AccountsDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* A retail nursery's shop: its tiles open the shared stock pages. */}
              <Route
                path="/retail"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="retail" moduleName="Retail Nursery">
                      <RetailDashboard />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/customers"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['picker', 'operator', 'manager', 'admin']} retail={['staff', 'manager']} what="The customer list">
                      <NurseryCustomerList />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/customers/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryCustomerForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/customers/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryCustomerForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/customers/:id"
                element={
                  <ProtectedRoute>
                    <NurseryCustomerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/price-lists"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['manager', 'admin']} retail={['manager']} what="The price lists">
                      <NurseryPriceListList />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/price-lists/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts" alsoModules={retailAlso('admin')}>
                      <NurseryPriceListForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/price-lists/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts" alsoModules={retailAlso('admin')}>
                      <NurseryPriceListForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/invoices"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryInvoiceList />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/invoices/:id"
                element={
                  <ProtectedRoute>
                    <AccountsOrNurseryGate nursery={['picker', 'operator', 'manager', 'admin']} retail={['staff', 'manager']} what="An invoice">
                      <NurseryInvoiceDetail />
                    </AccountsOrNurseryGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/receivables"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryReceivables />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/rebates"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <NurseryRebates />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/remittances"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <Remittances />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/suppliers"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts" alsoModules={retailAlso('admin')}>
                      <Suppliers />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* No licence gate: an org admin with no accounts seat approves
                  credit notes, and the page asks the server who may. */}
              <Route
                path="/accounts/credit-note-requests"
                element={
                  <ProtectedRoute>
                    <CreditNoteRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/purchase-order-users"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <PurchaseOrderPeople />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/account-rules"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <AccountRules />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/deposits"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <Deposits />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/terms"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <TradingTerms />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/stripe"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <CardPayments />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/xero"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <XeroSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/myob"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <MyobSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts/payroll-mappings"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="accounts" moduleName="Accounts">
                      <PayrollExportMappings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              {/* Where these pages used to live. Bookmarks, emailed links and an
                  app bundle older than the move still arrive on these. */}
              <Route path="/nursery/customers" element={<MovedRoute to="/accounts/customers" />} />
              <Route path="/nursery/customers/new" element={<MovedRoute to="/accounts/customers/new" />} />
              <Route path="/nursery/customers/:id/edit" element={<MovedRoute to="/accounts/customers/:id/edit" />} />
              <Route path="/nursery/customers/:id" element={<MovedRoute to="/accounts/customers/:id" />} />
              <Route path="/nursery/price-lists" element={<MovedRoute to="/accounts/price-lists" />} />
              <Route path="/nursery/price-lists/new" element={<MovedRoute to="/accounts/price-lists/new" />} />
              <Route path="/nursery/price-lists/:id/edit" element={<MovedRoute to="/accounts/price-lists/:id/edit" />} />
              <Route path="/nursery/invoices" element={<MovedRoute to="/accounts/invoices" />} />
              <Route path="/nursery/invoices/:id" element={<MovedRoute to="/accounts/invoices/:id" />} />
              <Route path="/nursery/receivables" element={<MovedRoute to="/accounts/receivables" />} />
              <Route path="/nursery/rebates" element={<MovedRoute to="/accounts/rebates" />} />
              <Route path="/nursery/xero" element={<MovedRoute to="/accounts/xero" />} />
              <Route path="/admin/xero-payroll-settings" element={<MovedRoute to="/accounts/xero" />} />
              {/* Payroll and invoicing share one Xero connection, on one page. */}
              <Route path="/accounts/xero-payroll" element={<MovedRoute to="/accounts/xero" />} />
              <Route path="/admin/myob-settings" element={<MovedRoute to="/accounts/myob" />} />
              <Route path="/admin/payroll-mappings" element={<MovedRoute to="/accounts/payroll-mappings" />} />
              {/* Customer orders — auth only, no nursery license needed */}
              <Route
                path="/nursery/my-orders"
                element={
                  <ProtectedRoute>
                    <CustomerOrdersDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Equipment Routes — read/use available to all authenticated users */}
              <Route
                path="/equipment"
                element={
                  <ProtectedRoute>
                    <EquipmentList />
                  </ProtectedRoute>
                }
              />
              {/* Insurance — anyone reads; the forms need a vehicles creator or admin seat */}
              <Route
                path="/equipment/insurance"
                element={
                  <ProtectedRoute>
                    <InsurancePolicies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <InsurancePolicyForm key="new" mode="new" />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/:id"
                element={
                  <ProtectedRoute>
                    <InsurancePolicyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <InsurancePolicyForm key="edit" mode="edit" />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/insurance/:id/renew"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <InsurancePolicyForm key="renew" mode="renew" />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/pre-start"
                element={
                  <ProtectedRoute>
                    <PreStartFlow />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/sessions"
                element={
                  <ProtectedRoute>
                    <SessionHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/services"
                element={
                  <ProtectedRoute>
                    <ServiceRecords />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id"
                element={
                  <ProtectedRoute>
                    <EquipmentDetail />
                  </ProtectedRoute>
                }
              />

              {/* Parts catalogue — any authenticated user can read, write requires license */}
              <Route
                path="/equipment/parts"
                element={
                  <ProtectedRoute>
                    <PartsCatalog />
                  </ProtectedRoute>
                }
              />

              {/* Equipment management — Vehicles module license required */}
              <Route
                path="/equipment/admin"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <EquipmentAdminSettings />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/new"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <EquipmentForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/edit"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <EquipmentForm />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/maintenance"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <MaintenanceManagement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipment/:id/attach"
                element={
                  <ProtectedRoute>
                    <LicenseGate moduleKey="vehicles" moduleName="Vehicles">
                      <AttachImplement />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              />

              {/* Redirect any unknown routes */}
              <Route
                path="*"
                element={<Navigate to={Capacitor.isNativePlatform() ? '/login' : '/'} replace />}
              />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
